import { useCallback, useMemo } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';

import { RightOutlined } from '@ant-design/icons';
import { Table as AntdTable, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import Truncate from 'v2/components/Shared/TruncateText';
import type { IPartnerCommission } from 'v2/interfaces/reports/partnerCommissions';
import { partnerCommissionsService } from 'v2/services/reports/partnerCommissions';

interface IProps {
  handleSeeDetailsClick: (partner_id: number) => void;
}

export const Table = ({ handleSeeDetailsClick }: IProps) => {
  useObservable(() => partnerCommissionsService.loadCommissions(), []);
  const [commissions] = useObservable(() => partnerCommissionsService.getCommissions(), []);
  const [isLoading] = useObservable(() => partnerCommissionsService.isLoading(), []);
  const [pagination] = useObservable(() => partnerCommissionsService.getPaginationResponse(), []);

  const onChangePerPage = useCallback(
    (perPage: number) => {
      partnerCommissionsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
      document.getElementById('commissions-partners-table')?.scrollIntoView();
    },
    [pagination]
  );

  const onChangePage = useCallback(
    (page: number) => {
      partnerCommissionsService.setPagination({ ...pagination, current_page: page });
      document.getElementById('commissions-partners-table')?.scrollIntoView();
    },
    [pagination]
  );

  const columns: ColumnsType<IPartnerCommission> = useMemo(
    () => [
      {
        title: 'Parceiro (coprodutor)',
        key: 'partner',
        render: (_: unknown, commission) => {
          if (!commission?.name) {
            return <span>-</span>;
          }

          return (
            <>
              <span className='block'>
                <Truncate count={25} final='...'>
                  {commission?.name || ''}
                </Truncate>
              </span>
              <span className='block'>
                <Truncate count={25} final='...'>
                  {commission?.email || ''}
                </Truncate>
              </span>
            </>
          );
        }
      },
      {
        title: 'Quantidade de Comissões',
        dataIndex: 'total_commissions',
        key: 'total_commissions',
        align: 'right',
        render: (value: number) => <span className='text-xs'>{value ?? 0}</span>
      },
      {
        title: 'Total de comissão paga (líquido)',
        dataIndex: 'total_gain',
        key: 'total_gain',
        align: 'right',
        render: (value: number) => (
          <span className='font-semibold'>
            <FormattedNumber value={value ?? 0} format='BRL' />
          </span>
        )
      },
      {
        title: '',
        key: 'actions',
        fixed: 'right',
        width: 60,
        align: 'right',
        render: (_: unknown, commission) => (
          <Button type='text' icon={<RightOutlined />} onClick={() => handleSeeDetailsClick(commission.id)} />
        )
      }
    ],
    [handleSeeDetailsClick]
  );

  return (
    <div id='commissions-partners-table' data-testid='commissions-partners-table' className='overflow-x-auto'>
      <AntdTable<IPartnerCommission>
        rowKey='id'
        columns={columns}
        dataSource={commissions || []}
        loading={!!isLoading}
        scroll={{ x: 700 }}
        pagination={{
          current: pagination?.current_page || 1,
          total: pagination?.total_items || 0,
          pageSize: pagination?.per_page || 10,
          pageSizeOptions: ['10', '25', '50'],
          showSizeChanger: true,
          onChange: page => onChangePage(page),
          onShowSizeChange: (_, size) => onChangePerPage(size)
        }}
        locale={{ emptyText: 'Nenhum registro encontrado.' }}
      />
    </div>
  );
};
